import React, { useState, useEffect } from 'react';
import { HashRouter } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import AppContent from './components/AppContent';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// Axios interceptor - attach JWT token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 by clearing token
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const detail = error.response.data?.detail;
      sessionStorage.removeItem('token');
      if (detail === 'SESSION_REPLACED') {
        sessionStorage.setItem('logout_reason', 'session_replaced');
      }
      window.location.hash = '#/ratebook';
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Root-level Error Boundary — catches ANY render crash in the entire app
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('=== APP RENDER CRASH ===');
    console.error('Error:', error?.message);
    console.error('Stack:', error?.stack);
    console.error('Component stack:', info?.componentStack);
    this.setState({ info });
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || 'Unknown error';
      return (
        <div style={{ padding: '32px', fontFamily: 'sans-serif', maxWidth: '600px', margin: '60px auto' }}>
          <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '12px', padding: '24px' }}>
            <h2 style={{ color: '#991b1b', marginBottom: '12px', fontSize: '18px' }}>Something went wrong</h2>
            <p style={{ color: '#7f1d1d', fontSize: '14px', marginBottom: '16px', wordBreak: 'break-all' }}>
              <strong>Error:</strong> {msg}
            </p>
            <details style={{ marginBottom: '16px' }}>
              <summary style={{ cursor: 'pointer', color: '#b91c1c', fontSize: '13px' }}>Technical details</summary>
              <pre style={{ fontSize: '11px', color: '#7f1d1d', marginTop: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {this.state.error?.stack}
              </pre>
            </details>
            <button
              onClick={() => { this.setState({ hasError: false, error: null, info: null }); }}
              style={{ padding: '10px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', marginRight: '8px' }}
            >
              Try Again
            </button>
            <button
              onClick={() => { window.location.hash = '#/ratebook'; window.location.reload(); }}
              style={{ padding: '10px 20px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Go to Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('token');
      if (token) {
        try {
          const res = await axiosInstance.get('/auth/me');
          setUser(res.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          sessionStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (userData, token) => {
    sessionStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/auth/invalidate-session');
    } catch (e) {
      // Ignore - token might already be invalid
    }
    sessionStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-amber-900 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <HashRouter>
        <Toaster position="top-right" />
        <AppContent
          user={user}
          setUser={setUser}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
        />
      </HashRouter>
    </AppErrorBoundary>
  );
}

export default App;
